import React, { useState, useEffect } from 'react';
import { planTemplateAPI, branchAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './PlanTemplates.css';

const CATEGORIES = [
    { value: 'basic_guest', label: 'Basic Guest' },
    { value: 'premium_guest', label: 'Premium Guest' },
    { value: 'vip', label: 'VIP' },
    { value: 'staff', label: 'Staff' },
    { value: 'conference', label: 'Conference' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'family', label: 'Family' },
    { value: 'custom', label: 'Custom' },
];

const DEFAULT_FORM = {
    name: '',
    category: 'custom',
    description: '',
    bandwidth: { uploadSpeed: 1024, downloadSpeed: 2048 },
    dataLimit: { total: 0, daily: 0, monthly: 0 },
    sessionLimits: { maxDuration: 0, idleTimeout: 15, maxConcurrentSessions: 1 },
    validity: { duration: 24, unit: 'hours' },
    queueType: 'per_user',
    pricing: { amount: 0, currency: 'INR' },
    deviceLimit: 1,
    isDefault: false,
    isActive: true,
};

const PlanTemplates = () => {
    const { selectedBranch } = useBranchStore();
    const [templates, setTemplates] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [applyTarget, setApplyTarget] = useState(null);
    const [applyBranchId, setApplyBranchId] = useState('');

    useEffect(() => {
        fetchAll();
    }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch?._id;
            const [tplRes, branchRes] = await Promise.all([
                planTemplateAPI.getAll(branchId ? { branchId } : undefined),
                branchAPI.getAll(),
            ]);
            setTemplates(tplRes.data.data || []);
            setBranches(branchRes.data.data.branches || []);
        } catch (error) {
            toast.error('Failed to fetch plan templates');
        } finally {
            setLoading(false);
        }
    };

    const setField = (path, value) => {
        setFormData(prev => {
            const next = { ...prev };
            const keys = path.split('.');
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) {
                obj[keys[i]] = { ...obj[keys[i]] };
                obj = obj[keys[i]];
            }
            obj[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const handleNew = () => {
        setEditing(null);
        setFormData(DEFAULT_FORM);
        setShowModal(true);
    };

    const handleEdit = (tpl) => {
        setEditing(tpl);
        setFormData({
            name: tpl.name,
            category: tpl.category,
            description: tpl.description || '',
            bandwidth: { ...tpl.bandwidth },
            dataLimit: tpl.dataLimit || { total: 0, daily: 0, monthly: 0 },
            sessionLimits: tpl.sessionLimits || { maxDuration: 0, idleTimeout: 15, maxConcurrentSessions: 1 },
            validity: { ...tpl.validity },
            queueType: tpl.queueType || 'per_user',
            pricing: { ...tpl.pricing },
            deviceLimit: tpl.deviceLimit || 1,
            isDefault: !!tpl.isDefault,
            isActive: tpl.isActive !== false,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return toast.warning('Name is required');
        try {
            if (editing) {
                await planTemplateAPI.update(editing._id, formData);
                toast.success('Template updated');
            } else {
                await planTemplateAPI.create(formData);
                toast.success('Template created');
            }
            setShowModal(false);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (tpl) => {
        if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
        try {
            await planTemplateAPI.delete(tpl._id);
            toast.success('Template deleted');
            fetchAll();
        } catch (error) {
            toast.error('Failed to delete template');
        }
    };

    const handleClone = async (tpl) => {
        try {
            await planTemplateAPI.clone(tpl._id, { name: `${tpl.name} (Copy)` });
            toast.success('Template cloned');
            fetchAll();
        } catch (error) {
            toast.error('Failed to clone template');
        }
    };

    const openApply = (tpl) => {
        setApplyTarget(tpl);
        setApplyBranchId(branches[0]?._id || '');
        setShowApplyModal(true);
    };

    const handleApply = async () => {
        if (!applyBranchId) return toast.warning('Select a branch');
        try {
            await planTemplateAPI.applyToBranch(applyTarget._id, { branchId: applyBranchId });
            toast.success(`Plan created in ${branches.find(b => b._id === applyBranchId)?.name}`);
            setShowApplyModal(false);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to apply template');
        }
    };

    const categoryLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || v;

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-layer-group"></i>
                            Plan Templates
                        </h1>
                        <p className="page-subtitle-modern">Reusable plan blueprints — clone or apply to any branch instead of building a plan from scratch</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}>
                            <i className="fas fa-plus-circle"></i>
                            New Template
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading templates...</p></div>
            ) : templates.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-layer-group"></i></div>
                    <h3>No Plan Templates Yet</h3>
                    <p>Create reusable templates like "Premium Guest" or "Conference" to quickly spin up plans across branches.</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {templates.map(tpl => (
                        <div key={tpl._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-avatar" style={{ background: '#14263f15', color: '#14263f' }}>
                                    <i className="fas fa-layer-group"></i>
                                </div>
                                <div className="resource-title">
                                    <h3>{tpl.name}</h3>
                                    <span className="resource-subtitle">{categoryLabel(tpl.category)} · v{tpl.version}</span>
                                </div>
                                <div className="card-actions">
                                    <button className="action-btn-icon edit" onClick={() => handleEdit(tpl)} title="Edit"><i className="fas fa-edit"></i></button>
                                    <button className="action-btn-icon" onClick={() => handleClone(tpl)} title="Clone"><i className="fas fa-clone"></i></button>
                                    <button className="action-btn-icon delete" onClick={() => handleDelete(tpl)} title="Delete"><i className="fas fa-trash"></i></button>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="status-badges">
                                    <span className={`status-badge ${tpl.isActive ? 'active' : 'inactive'}`}>
                                        <span className="badge-dot"></span>{tpl.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className="status-badge inactive">{tpl.queueType?.replace('_', ' ')}</span>
                                </div>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <i className="fas fa-tachometer-alt"></i>
                                        <div><span className="info-label">Speed</span><span className="info-value">{tpl.bandwidth.downloadSpeed}/{tpl.bandwidth.uploadSpeed} Kbps</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-clock"></i>
                                        <div><span className="info-label">Validity</span><span className="info-value">{tpl.validity.duration} {tpl.validity.unit}</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-laptop"></i>
                                        <div><span className="info-label">Devices</span><span className="info-value">{tpl.deviceLimit}</span></div>
                                    </div>
                                    <div className="info-item">
                                        <i className="fas fa-rupee-sign"></i>
                                        <div><span className="info-label">Price</span><span className="info-value">₹{tpl.pricing.amount}</span></div>
                                    </div>
                                </div>
                                <button className="btn-primary-modern" style={{ width: '100%', marginTop: '0.75rem' }} onClick={() => openApply(tpl)}>
                                    <i className="fas fa-magic"></i> Apply to Branch
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editing ? 'Edit Template' : 'New Plan Template'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" value={formData.name} onChange={e => setField('name', e.target.value)} required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Category</label>
                                        <select value={formData.category} onChange={e => setField('category', e.target.value)}>
                                            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Description</label>
                                        <input type="text" value={formData.description} onChange={e => setField('description', e.target.value)} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Download Speed (Kbps) *</label>
                                        <input type="number" value={formData.bandwidth.downloadSpeed} onChange={e => setField('bandwidth.downloadSpeed', Number(e.target.value))} required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Upload Speed (Kbps) *</label>
                                        <input type="number" value={formData.bandwidth.uploadSpeed} onChange={e => setField('bandwidth.uploadSpeed', Number(e.target.value))} required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Validity</label>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <input type="number" value={formData.validity.duration} onChange={e => setField('validity.duration', Number(e.target.value))} required />
                                            <select value={formData.validity.unit} onChange={e => setField('validity.unit', e.target.value)}>
                                                <option value="minutes">Minutes</option>
                                                <option value="hours">Hours</option>
                                                <option value="days">Days</option>
                                                <option value="months">Months</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Device Limit</label>
                                        <input type="number" min="1" value={formData.deviceLimit} onChange={e => setField('deviceLimit', Number(e.target.value))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Data Limit Total (MB)</label>
                                        <input type="number" value={formData.dataLimit.total} onChange={e => setField('dataLimit.total', Number(e.target.value))} placeholder="0 = unlimited" />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Max Concurrent Sessions</label>
                                        <input type="number" min="1" value={formData.sessionLimits.maxConcurrentSessions} onChange={e => setField('sessionLimits.maxConcurrentSessions', Number(e.target.value))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Idle Timeout (minutes)</label>
                                        <input type="number" value={formData.sessionLimits.idleTimeout} onChange={e => setField('sessionLimits.idleTimeout', Number(e.target.value))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Queue Type</label>
                                        <select value={formData.queueType} onChange={e => setField('queueType', e.target.value)}>
                                            <option value="per_user">Per User</option>
                                            <option value="shared">Shared</option>
                                            <option value="dynamic">Dynamic</option>
                                        </select>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Price (₹)</label>
                                        <input type="number" min="0" value={formData.pricing.amount} onChange={e => setField('pricing.amount', Number(e.target.value))} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={formData.isActive} onChange={e => setField('isActive', e.target.checked)} />
                                            <span>Active</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern"><i className="fas fa-check"></i> {editing ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showApplyModal && (
                <div className="modal-overlay-modern" onClick={() => setShowApplyModal(false)}>
                    <div className="modal-modern modal-small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>Apply "{applyTarget?.name}" to Branch</h2>
                            <button className="modal-close" onClick={() => setShowApplyModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="form-group-modern">
                                <label>Branch</label>
                                <select value={applyBranchId} onChange={e => setApplyBranchId(e.target.value)}>
                                    <option value="">Select Branch</option>
                                    {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                </select>
                                <p className="input-hint">Creates a new, real Plan in this branch with all of this template's settings.</p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowApplyModal(false)}>Cancel</button>
                            <button className="btn-primary-modern" onClick={handleApply}><i className="fas fa-magic"></i> Create Plan</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanTemplates;
